/**
 * The fleet feed feature (M6 plan P35, spec §8 "Feed and alerts"; the career in the Reach only, with the strategic
 * client). Order `FEATURE_ORDER.fleetFeed` 529 (after the Reach HUD's toasts; 527 and 528 are the Fleet and
 * Captains screens).
 *
 * - **Alert actions:** binds P22's hooks (`bindFleetAlertHooks`): *jump to* opens the galaxy map on the alert's
 *   system (from the flight now, from the pause menu on the next unpaused frame); *send wing* orders the wing to
 *   break and engage. *Send wing* is offered only for an asset under attack in the player's current system while the
 *   wing has members (the rule in `model.ts`, D-413).
 * - **Flight strip:** under the toasts, the newest actionable alert with its keys (bindings.json keyboard `fleetAlertJump` H and `fleetAlertWing` I);
 *   a key press runs the action. Not while paused, docked (a hold) or under a modal.
 * - **Fleet feed panel:** the dashboard's **Fleet › Feed** section (dashboard spec §2.2, W3 card M2): recent alerts
 *   with buttons, then the captain logs and claim changes. Arrows and the pad move through its buttons
 *   (`navigateButtons`); *jump to* closes the dashboard and the map opens on the next frame.
 *
 * `frame` allocates nothing except on the open, close and action events (D-014).
 */
import { createElement, useEffect, useRef, type ReactElement } from 'react'
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { fleetAlertInbox, type FleetAlertAction } from '../../fleet-link/presence'
import { createPadNav, stepPadNav, type PadNavState } from '../../hud/menu'
import { anyKeyHeld } from '../../reach/input'
import { loadSettingsConfig } from '../../settings/settings'
import type { FleetAlert } from '../../strategic/types'
import { closeDashboard, openDashboard, registerDashSection, type DashSectionProps } from '../../ui/dashboard/registry'
import { fleetSection, FLEET_SECTION_IDS, useButtonNav, useLive } from '../../ui/fleet/sectionHost'
import { navigateButtons } from '../../ui/strategic/mount'
import { activeWing, commandWing } from '../../wing/subsystem'
import mapsFeature from '../maps/feature'
import { bindAlertActions, createAlertStrip, createAlertStripView, dispatchAlert, stepAlertStrip, writeAlertStrip, type AlertStrip, type AlertStripView } from './alerts'
import { FeedPanel, type FeedAlertRow } from './FeedPanel'
import { loadFleetFeedConfig, offersAction, type AlertContext, type AlertKeys, type FleetFeedConfig } from './model'
import { createFeedController, type FeedController } from './panel'
import './fleetFeed.css'

export interface FleetFeedFeature extends GameFeature {
  readonly strip: AlertStrip | null
  readonly view: AlertStripView | null
  readonly feed: FeedController | null
  /** True while the Fleet › Feed section is on screen. */
  readonly panelOpen: boolean
  /** Opens the dashboard at Fleet › Feed (tests, the debug API). */
  openFeed(): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface FleetFeedOptions {
  /** Opens the galaxy map focused on a system (default: the maps feature). */
  openMap?: (ctx: GameContext, systemId: string) => void
}

/** A history alert's stable key (indexes shift when a new alert arrives). */
function alertKey(a: Readonly<FleetAlert>): string {
  return `${a.t}|${a.kind}|${a.systemId}|${a.shipId ?? ''}|${a.stationId ?? ''}`
}

export function createFleetFeedFeature(opts: FleetFeedOptions = {}): FleetFeedFeature {
  const openMap = opts.openMap ?? ((ctx: GameContext, id: string): void => mapsFeature.open(ctx, id))
  let cfg: FleetFeedConfig | null = null
  /** The strip's hotkeys (bindings.json keyboard `fleetAlertJump`, `fleetAlertWing`), read at init. */
  let keys: AlertKeys = { jumpTo: [], sendWing: [] }
  let strip: AlertStrip | null = null
  let view: AlertStripView | null = null
  let feed: FeedController | null = null
  let nav: PadNavState | null = null
  /** The mounted Feed section's panel (null while not on screen). */
  let panel: HTMLElement | null = null
  let pendingMap: string | null = null
  let jumpWasDown = true
  let wingWasDown = true
  const actx: AlertContext = { systemId: null, wingCount: 0 }
  const offs: (() => void)[] = []

  function updateContext(ctx: GameContext): void {
    actx.systemId = ctx.reach?.session.layout?.systemId ?? null
    actx.wingCount = activeWing()?.count ?? 0
  }

  function alertRows(): FeedAlertRow[] {
    const s = strip
    if (s === null) return []
    return s.history.map((a) => ({ key: alertKey(a), text: a.text, jump: offersAction(a, 'jumpTo', actx), wing: offersAction(a, 'sendWing', actx) }))
  }

  const subscribe = (fn: () => void): (() => void) => feed?.onChange(fn) ?? ((): void => undefined)
  const refresh = (): unknown => feed?.refresh()

  /** The Fleet › Feed section body: re-reads the feed every `refreshS` while on screen. */
  function FeedSection({ nav }: DashSectionProps): ReactElement | null {
    const ref = useRef<HTMLDivElement>(null)
    useLive(subscribe, refresh, (cfg?.refreshS ?? 0) * 1000)
    useButtonNav(nav, ref)
    useEffect(() => {
      panel = ref.current
      return (): void => {
        panel = null
      }
    }, [])
    const c = cfg
    if (c === null) return null
    return createElement(FeedPanel, {
      labels: c.labels,
      alerts: alertRows(),
      rows: feed?.rows ?? [],
      panelRef: ref,
      onAction: (key: string, action: FleetAlertAction) => {
        const a: FleetAlert | undefined = strip?.history.find((h) => alertKey(h) === key)
        if (a === undefined) return
        // Jump to leaves the dashboard for the map (it opens on the next frame); send wing stays.
        if (dispatchAlert(a, action, actx) && action === 'jumpTo') closeDashboard(false)
      },
    })
  }

  const feature: FleetFeedFeature = {
    name: 'fleetFeed',
    order: FEATURE_ORDER.fleetFeed,
    get strip(): AlertStrip | null {
      return strip
    },
    get view(): AlertStripView | null {
      return view
    },
    get feed(): FeedController | null {
      return feed
    },
    get panelOpen(): boolean {
      return panel !== null
    },

    init(ctx: GameContext): void {
      const client = ctx.services.maybe('strategic')
      if (client === undefined || ctx.reach === null) return
      const c = loadFleetFeedConfig()
      cfg = c
      keys = { jumpTo: ctx.data.bindings.keyboard.fleetAlertJump, sendWing: ctx.data.bindings.keyboard.fleetAlertWing }
      strip = createAlertStrip(fleetAlertInbox)
      view = createAlertStripView(ctx.hudRoot)
      nav = createPadNav(loadSettingsConfig().menuNav, ctx.data.bindings.gamepad.buttons.pause)
      const systems = new Map(ctx.reach.galaxy.systems.map((s) => [s.id, s.name]))
      feed = createFeedController(
        { fleetFeed: (n) => client.fleetFeed(n), corpView: () => client.corpView(), clock: () => client.clock },
        c,
        (id) => systems.get(id) ?? id,
      )
      // The claim baseline: changes from here on are news.
      void feed.refresh()
      offs.push(
        bindAlertActions({
          openMap: (id) => {
            pendingMap = id
          },
          commandWing: (cmd) => commandWing(cmd),
        }),
      )
      offs.push(registerDashSection(fleetSection('feed', FeedSection)))
    },

    openFeed(): void {
      openDashboard({ tab: 'fleet', section: FLEET_SECTION_IDS.feed })
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const s = strip
      const v = view
      const c = cfg
      if (s === null || v === null || c === null) return
      updateContext(ctx)
      stepAlertStrip(s, fleetAlertInbox, f.paused ? 0 : f.dt, c)
      writeAlertStrip(v, s, actx, c, keys)

      const i = nav === null ? null : stepPadNav(nav, ctx.input.pad, f.dt)
      if (panel !== null) {
        if (i !== null && i !== 'back' && i !== 'start' && !f.paused) navigateButtons(panel, i)
        return
      }

      if (pendingMap !== null && !f.paused && ctx.ui.modal === 0) {
        const id = pendingMap
        pendingMap = null
        openMap(ctx, id)
      }

      // Flight hotkeys: an edge on the key, only in flight (not paused, docked or under a modal).
      const jumpDown = anyKeyHeld(ctx.input.keys, keys.jumpTo)
      const wingDown = anyKeyHeld(ctx.input.keys, keys.sendWing)
      const jumpPressed = jumpDown && !jumpWasDown
      const wingPressed = wingDown && !wingWasDown
      jumpWasDown = jumpDown
      wingWasDown = wingDown
      if (f.paused || f.held === true || ctx.ui.modal !== 0 || s.alert === null) return
      // Jump to sets `pendingMap` (the hook); the map opens on the next frame.
      if (jumpPressed) dispatchAlert(s.alert, 'jumpTo', actx)
      else if (wingPressed) dispatchAlert(s.alert, 'sendWing', actx)
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      view?.el.remove()
      view = null
      strip = null
      feed = null
      cfg = null
      nav = null
      pendingMap = null
    },
  }
  return feature
}

const feature: FleetFeedFeature = createFleetFeedFeature()

export default feature
